import gql from 'graphql-tag';

export const CREATE_INVESTMENT = gql`
  mutation adminCreateInvestment(
    $data: AdminInvestmentInput!
    $image: Upload
    $documents: [Upload]
  ) {
    adminCreateInvestment(data: $data, image: $image, documents: $documents) {
      _id
      address
      investorYieldMaturity
      investorYieldMaturityInfo
      borrowerPaymentInsurance
      borrowerPaymentInsuranceInfo
      servicingFee
      netYield
      investmentAmount
      investmentAmountInfo
      notePosition
      notePositionInfo
      asIsLoanToValue
      arvLoanToValue
      repaymentType
      termLength
      termLengthInfo
      loanPurpose
      loanPurposeInfo
      exitFee
      exitFeeInfo
      borrowerCreditScore
      propertyType
      numberOfUnits
      asIsValuePurchasePrice
      asIsLtv
      rehabBudget
      rehabBudgetInfo
      arv
      arvLtv
      riskRating
      riskRatingInfo
      dealOverview
      originalLoanTerm
      originalLoanTermInfo
      extensionOption
      extensionOptionInfo
      extensionFee
      extensionFeeInfo
      prepaymentPenalty
      prepaymentPenaltyInfo
      imageUrl
      city
      state
      zipCode
      termPeriod
      startDate
      nextCycleId
      previousCycleId
      createdAt
      updatedAt
      maturityDate
      documents {
        _id
        name
        url
        investmentId
        createdAt
        updatedAt
      }
    }
  }
`;

export const UPDATE_INVESTMENT = gql`
  mutation adminUpdateInvestment(
    $investmentId: String!
    $data: AdminInvestmentInput!
    $image: Upload
    $documents: [Upload]
  ) {
    adminUpdateInvestment(
      data: $data
      image: $image
      documents: $documents
      investmentId: $investmentId
    ) {
      _id
      address
      investorYieldMaturity
      investorYieldMaturityInfo
      servicingFee
      netYield
      investmentAmount
      investmentAmountInfo
      notePosition
      notePositionInfo
      asIsLoanToValue
      arvLoanToValue
      repaymentType
      termLength
      termLengthInfo
      loanPurpose
      loanPurposeInfo
      exitFee
      exitFeeInfo
      borrowerCreditScore
      propertyType
      numberOfUnits
      asIsValuePurchasePrice
      asIsLtv
      rehabBudget
      rehabBudgetInfo
      arv
      arvLtv
      riskRating
      riskRatingInfo
      dealOverview
      originalLoanTerm
      originalLoanTermInfo
      extensionOption
      extensionOptionInfo
      extensionFee
      extensionFeeInfo
      prepaymentPenalty
      prepaymentPenaltyInfo
      imageUrl
      city
      state
      zipCode
      termPeriod
      startDate
      nextCycleId
      previousCycleId
      createdAt
      updatedAt
      maturityDate
      documents {
        _id
        name
        url
        investmentId
        createdAt
        updatedAt
      }
      noteType
      borrowerTrackRecord
      paymentGuaranteeFee
    }
  }
`;

// Reconnected to constlending-api-next `adminCheckKyc` (replaces `checkKycByAdmin`): re-runs the Sila
// check on ONE account and returns the refreshed UserAccount (kyc + kycReasons), not the raw Sila payload.
export const ADMIN_CHECK_KYC = gql`
  mutation adminCheckKyc($userId: ID!, $userAccountId: ID!) {
    adminCheckKyc(data: { userId: $userId, userAccountId: $userAccountId }) {
      _id
      type
      accountName
      kyc
      kycReasons
      verificationUuid
    }
  }
`;

// Manual North Capital accreditation (RETOOL-GUIDE §4): `accredited: true` grants, `false` revokes.
// `reason` is mandatory (>= 10 chars after trim) and audited with the admin taken from the token.
// Acts on the person's INDIVIDUAL account (NOT_FOUND when the user has none) and returns it.
export const ADMIN_SET_ACCREDITATION = gql`
  mutation adminSetAccreditation($data: AdminSetAccreditationInput!) {
    adminSetAccreditation(data: $data) {
      _id
      userId
      type
      accreditedInvestorStatus
    }
  }
`;

// PATCH of one product rule (RETOOL-GUIDE §5.2): only the fields sent are replaced and the backend
// validates the MERGED row (default >= lowest tier). `reason` is mandatory (10-500 chars after trim) and
// audited with the admin taken from the token. Never creates a rule: a product without a row is NOT_FOUND.
export const ADMIN_SET_INVESTMENT_RULE = gql`
  mutation adminSetInvestmentRule($data: AdminSetInvestmentRuleInput!) {
    adminSetInvestmentRule(data: $data) {
      productType
      firstTimeMinimum
      subsequentMinimum
      presets
      defaultAmount
      updatedAt
    }
  }
`;

// Grant or update one investor's exception (RETOOL-GUIDE §6.1). Keyed by the PERSON's `userId`; acts on
// their individual account (NOT_FOUND when they have none) and returns it with the exceptions in force.
// `minimum: 0` is legitimate (any amount). Granting again on the same product replaces the value.
export const ADMIN_SET_INVESTMENT_MINIMUM = gql`
  mutation adminSetInvestmentMinimum($data: AdminSetInvestmentMinimumInput!) {
    adminSetInvestmentMinimum(data: $data) {
      _id
      userId
      investmentMinimums {
        productType
        minimum
      }
    }
  }
`;

// Withdraw one exception (RETOOL-GUIDE §6.2): same key without `minimum`; the reason is mandatory here
// too (a withdrawal is audited like a grant). The investor goes back to the product rule.
export const ADMIN_REMOVE_INVESTMENT_MINIMUM = gql`
  mutation adminRemoveInvestmentMinimum(
    $data: AdminRemoveInvestmentMinimumInput!
  ) {
    adminRemoveInvestmentMinimum(data: $data) {
      _id
      userId
      investmentMinimums {
        productType
        minimum
      }
    }
  }
`;

// The note's UPDATE TIMELINE, the three verbs of one entry (backend: investment/modules/admin §
// timeline.service). All three answer with the NOTE, not with the entry, because the backend keeps the
// array ordered on every write (newest `date` first) — so the list is re-rendered from the response and
// there is nothing to re-sort here. Asking for `_id` + `updates` is enough: Apollo normalises the note
// by `_id` and the shorter, longer or re-ordered array lands on the `getInvestment` already on screen.
const NOTE_TIMELINE = `
  _id
  updates {
    _id
    type
    info
    date
    createdAt
  }
`;

// `date` is the day the thing HAPPENED (what the investor reads and what the timeline is ordered by), not
// the day it was typed — `createdAt` is that one and the backend stamps it.
export const ADD_INVESTMENT_UPDATE = gql`
  mutation adminAddInvestmentUpdate($data: AdminAddInvestmentUpdateInput!) {
    adminAddInvestmentUpdate(data: $data) {
      ${NOTE_TIMELINE}
    }
  }
`;

// Every box optional and applied only when it ARRIVES: the modal sends the ones the operator actually
// moved, so an untouched field is never restated — and an edit that moved nothing comes back as
// INVESTMENT_UPDATE_NO_CHANGES with its own copy instead of a silent save.
export const UPDATE_INVESTMENT_UPDATE = gql`
  mutation adminUpdateInvestmentUpdate($data: AdminUpdateInvestmentUpdateInput!) {
    adminUpdateInvestmentUpdate(data: $data) {
      ${NOTE_TIMELINE}
    }
  }
`;

export const REMOVE_INVESTMENT_UPDATE = gql`
  mutation adminRemoveInvestmentUpdate($data: AdminRemoveInvestmentUpdateInput!) {
    adminRemoveInvestmentUpdate(data: $data) {
      ${NOTE_TIMELINE}
    }
  }
`;

// Creates an ADMIN — a `user` carrying `isAdmin: true`. The legacy had no such operation: the flag was
// written by hand in Mongo and the phone enrolled by replaying the 2FA setup, which is why this exists.
// NOBODY types a password (TL, 2026-09-12): the account is created without a usable one and the platform
// emails the person its own password-recovery link, so `passwordEmailSent` — not a credential — is what
// the screen has to report. False means the account exists and cannot be signed into yet.
export const ADMIN_CREATE_ADMIN = gql`
  mutation adminCreateAdmin($data: AdminCreateAdminInput!) {
    adminCreateAdmin(data: $data) {
      passwordEmailSent
      admin {
        id
        email
        firstName
        lastName
        phoneNumber
        isAdmin
        createdAt
      }
    }
  }
`;

// Treasury (2026-09-17): the two money movements of the house's wallet. The amount is typed ONCE (the
// typed-again confirmation was retired on 2026-09-29) and every move carries an audited reason. The bank is chosen
// by the `accountName` Sila lists; the instrument never leaves the backend.
const TREASURY_MOVEMENT_FIELDS = `
      id
      direction
      amount
      bankAccountName
      status
      reason
      adminId
      transactionId
      createdAt
      settledAt
      failureReason
`;

export const ADMIN_TREASURY_DEPOSIT = gql`
  mutation adminTreasuryDeposit($data: TreasuryDepositInput!) {
    adminTreasuryDeposit(data: $data) {
      ${TREASURY_MOVEMENT_FIELDS}
    }
  }
`;

export const ADMIN_TREASURY_WITHDRAW = gql`
  mutation adminTreasuryWithdraw($data: TreasuryWithdrawInput!) {
    adminTreasuryWithdraw(data: $data) {
      ${TREASURY_MOVEMENT_FIELDS}
    }
  }
`;

// Note wallet → a bank of the house (2026-09-18): the legacy note page's "Withdraw". Same guards as the
// treasury moves (audited reason, live available-balance check); the note is the SOURCE, the money is
// the house's.
export const ADMIN_INVESTMENT_WITHDRAW = gql`
  mutation adminInvestmentWithdraw($data: TreasuryNoteWithdrawInput!) {
    adminInvestmentWithdraw(data: $data) {
      ${TREASURY_MOVEMENT_FIELDS}
      investmentId
    }
  }
`;
